import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../utils/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

const UserSchemeContext = createContext(null);

/**
 * Fetches the current user's scheme_id from Firestore ONCE and shares it
 * across all ProductCards via context — prevents N parallel listeners per card.
 */
export function UserSchemeProvider({ children }) {
    const [schemeId, setSchemeId] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            if (stored) {
                const parsed = JSON.parse(stored);
                return parsed?.scheme_id || parsed?.schema_id || parsed?.schemeId || null;
            }
        } catch (e) {}
        return null;
    });

    useEffect(() => {
        let unsubDoc = null;

        const unsubAuth = auth.onAuthStateChanged((user) => {
            // Clean up previous listener
            if (unsubDoc) { unsubDoc(); unsubDoc = null; }

            if (!user) {
                // Check localStorage as fallback before clearing
                try {
                    const stored = localStorage.getItem('user');
                    if (stored) {
                        const parsed = JSON.parse(stored);
                        const sId = parsed?.scheme_id || parsed?.schema_id || parsed?.schemeId || null;
                        if (sId) { setSchemeId(sId); return; }
                    }
                } catch (e) {}
                setSchemeId(null);
                return;
            }

            // Try uid first, fall back to email
            const tryListen = (key) => {
                const ref = doc(db, 'users', key);
                return onSnapshot(ref, (snap) => {
                    if (snap.exists()) {
                        const data = snap.data();
                        const sId = data.scheme_id || data.schema_id || data.schemeId || null;
                        setSchemeId(sId);
                    } else if (key === user.uid && user.email) {
                        // uid doc not found — try email
                        if (unsubDoc) { unsubDoc(); unsubDoc = null; }
                        unsubDoc = tryListen(user.email.toLowerCase());
                    } else {
                        setSchemeId(null);
                    }
                }, (err) => {
                    console.error('UserSchemeContext listener error:', err);
                });
            };

            unsubDoc = tryListen(user.uid);
        });

        return () => {
            unsubAuth();
            if (unsubDoc) unsubDoc();
        };
    }, []);

    return (
        <UserSchemeContext.Provider value={schemeId}>
            {children}
        </UserSchemeContext.Provider>
    );
}

export function useUserScheme() {
    return useContext(UserSchemeContext);
}
